import { useEffect, useCallback, useRef, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router';
import { cn } from '@rocco/utils/cn';
import { useScrollArrows } from '../hooks/useScrollArrows';
import { useDragScroll } from '../hooks/useDragScroll';
import { useIsBelowMd } from '@ui-rocco/hooks/use-is-below-md';

interface CategoryPillItem {
    id: number;
    title: string;
    link: string;
}

interface CategoryPillsProps {
    categories: CategoryPillItem[];
    isVisible?: boolean;
    /**
     * Pill colour theme. `light` (default) = light-grey pills for the standard
     * pages; `dark` = charcoal pills for the dark editorial browse (projects).
     */
    theme?: 'light' | 'dark';
    /**
     * Rendered as the first item INSIDE the scroller, so it scrolls away with
     * the pills. The mobile browse pages put the parent-category back pill here
     * — desktop pins it outside the scroller instead, so pass a node that is
     * hidden at md+ (or omit it there).
     */
    leading?: ReactNode;
}

/**
 * Returns a CSS mask-image value based on scroll overflow state.
 * Only applies edge fades where content actually overflows.
 */
function getMaskImage(
    canScrollLeft: boolean,
    canScrollRight: boolean,
): string | undefined {
    if (!canScrollLeft && !canScrollRight) return undefined;
    if (canScrollLeft && canScrollRight) {
        return 'linear-gradient(to right, transparent, black 48px, black calc(100% - 48px), transparent)';
    }
    if (canScrollLeft) {
        return 'linear-gradient(to right, transparent, black 48px, black 100%)';
    }
    return 'linear-gradient(to right, black, black calc(100% - 48px), transparent)';
}

/**
 * Pill shaped buttons used in the CategoryNavigationBar. Takes user to the category page when clicked.
 * Displays as a horizontally scrollable list with drag-to-scroll on desktop.
 * Supports keyboard navigation (ArrowLeft/Right, Home/End), scroll-snap alignment,
 * and native touch scrolling on mobile.
 * @param categories - The categories to display.
 * @param isVisible - Whether the pills are visible.
 */
export const CategoryPills = ({
    categories,
    isVisible = true,
    theme = 'light',
    leading,
}: CategoryPillsProps) => {
    const isDark = theme === 'dark';
    const location = useLocation();
    const belowMd = useIsBelowMd();
    const { scrollRef, canScrollLeft, canScrollRight, prefersReducedMotion } =
        useScrollArrows();

    // Enable drag-to-scroll on desktop, sharing the same scroll container ref
    useDragScroll({ externalRef: scrollRef });

    // Scroll policy: the offset belongs to the pill SET, not the page.
    // - Fresh mount (deep link / Back): centre the active pill so "you are
    //   here" is visible.
    // - The set changed (drilled down/up the tree): the old offset is
    //   meaningless against new pills — reset to the left edge.
    // - Same set (leaf sibling switch): the user tapped a pill they could
    //   see; moving the strip under their thumb would be disorienting, so
    //   leave the offset alone.
    const pillSetSignature = categories.map((c) => c.id).join(',');
    const prevPillSetRef = useRef<string | null>(null);
    useEffect(() => {
        const container = scrollRef.current;
        if (!container) return;
        const prevPillSet = prevPillSetRef.current;
        prevPillSetRef.current = pillSetSignature;

        if (prevPillSet !== null && prevPillSet !== pillSetSignature) {
            container.scrollTo({ left: 0 });
            return;
        }
        if (prevPillSet !== null) {
            // Same set — sibling switch; keep the user's scroll position.
            return;
        }

        const rafId = requestAnimationFrame(() => {
            const activeEl = container.querySelector<HTMLElement>(
                '[aria-current="page"]',
            );
            if (!activeEl) return;
            // Centre the active pill by scrolling ONLY this horizontal container.
            // `scrollIntoView` also scrolls scrollable ancestors and the window
            // vertically, which fights browser-back scroll restoration on the
            // browse grid (scroll deep → open a tile → Back would jump up here).
            const containerRect = container.getBoundingClientRect();
            const elRect = activeEl.getBoundingClientRect();
            const target =
                container.scrollLeft +
                (elRect.left - containerRect.left) -
                (container.clientWidth - elRect.width) / 2;
            container.scrollTo({
                left: Math.max(0, target),
                behavior: prefersReducedMotion ? 'auto' : 'smooth',
            });
        });

        return () => cancelAnimationFrame(rafId);
    }, [location.pathname, pillSetSignature, scrollRef, prefersReducedMotion]);

    // Keyboard navigation: ArrowLeft/Right scroll by one pill, Home/End go to edges
    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLUListElement>) => {
            const container = scrollRef.current;
            if (!container) return;

            const behavior: ScrollBehavior = prefersReducedMotion
                ? 'auto'
                : 'smooth';
            // Measure actual pill step from DOM to avoid hardcoded gap values
            const pills = container.querySelectorAll('li');
            const pillWidth =
                pills.length >= 2
                    ? (pills[1] as HTMLElement).offsetLeft -
                      (pills[0] as HTMLElement).offsetLeft
                    : (pills[0]?.getBoundingClientRect().width ?? 150);

            switch (e.key) {
                case 'ArrowRight':
                    e.preventDefault();
                    container.scrollBy({ left: pillWidth, behavior });
                    break;
                case 'ArrowLeft':
                    e.preventDefault();
                    container.scrollBy({ left: -pillWidth, behavior });
                    break;
                case 'Home':
                    e.preventDefault();
                    container.scrollTo({ left: 0, behavior });
                    break;
                case 'End':
                    e.preventDefault();
                    container.scrollTo({
                        left: container.scrollWidth,
                        behavior,
                    });
                    break;
            }
        },
        [scrollRef, prefersReducedMotion],
    );

    if (!categories.length) {
        return null;
    }

    const maskImage = getMaskImage(canScrollLeft, canScrollRight);

    return (
        <div
            className={cn(
                'relative min-w-0 flex-1',
                isVisible
                    ? 'opacity-100 transition-all duration-300'
                    : 'w-0 overflow-hidden opacity-0 transition-all duration-300',
            )}
        >
            <ul
                ref={scrollRef as React.RefObject<HTMLUListElement | null>}
                tabIndex={0}
                onKeyDown={handleKeyDown}
                className={cn(
                    'scrollbar-hide flex items-center gap-3 overflow-x-auto px-3',
                    'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-hidden',
                    // Only when pills overflow — avoids grab cursor across empty flex-1 space
                    (canScrollLeft || canScrollRight) &&
                        'cursor-grab active:cursor-grabbing',
                    // Mobile scrollers are full-bleed with a hard edge (same as
                    // the carousels) — the ! beats the inline maskImage style.
                    'max-md:mask-none!',
                )}
                aria-label="Category navigation"
                style={
                    maskImage
                        ? { maskImage, WebkitMaskImage: maskImage }
                        : undefined
                }
            >
                {/* md:hidden — the slot exists for mobile in-scroll chrome;
                    desktop pins its equivalents outside the scroller. */}
                {leading ? (
                    <li className="shrink-0 md:hidden">{leading}</li>
                ) : null}
                {categories.map((category) => (
                    <li key={category.id}>
                        <NavLink
                            to={category.link}
                            preventScrollReset={!belowMd}
                            className={({ isActive }) =>
                                cn(
                                    'flex h-10.5 shrink-0 items-center justify-center rounded-full px-5.25 text-base whitespace-nowrap transition-colors',
                                    'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-hidden',
                                    isDark
                                        ? isActive
                                            ? 'bg-white text-[#181818] hover:bg-white/90 active:bg-white/80'
                                            : 'bg-[#323232] text-white hover:bg-[#3d3d3d] active:bg-[#484848]'
                                        : isActive
                                          ? 'bg-[#161616] text-white hover:bg-[#2a2a2a] active:bg-[#333333]'
                                          : 'bg-[#f2f2f2] text-[#323232] hover:bg-[#e8e8e8] active:bg-[#dedede]',
                                )
                            }
                        >
                            {category.title}
                        </NavLink>
                    </li>
                ))}
            </ul>
        </div>
    );
};
